import React, { useState, useEffect, useCallback } from 'react';

const LandingPage = () => {
  // Gallery images data
  const galleryImages = [
    { src: '/bb_0321-2.jpeg', alt: 'Basketball Practice March 2021' },
    { src: '/first_scaya_tournament_0321-2.jpeg', alt: 'First SCAYA Tournament March 2021' },
    { src: '/soccer_0321-2.jpeg', alt: 'Soccer Practice March 2021' },
    { src: '/soccer_0625-2.jpeg', alt: 'Soccer Game June 2025' },
    { src: '/IMG_4476-2.jpeg', alt: 'SCAYA Community Event' },
    { src: '/IMG_4777-2.jpeg', alt: 'Youth Sports Program' },
    { src: '/IMG_0089-2.jpeg', alt: 'Team Building Activity' },
    { src: '/IMG_0098-2.jpeg', alt: 'SCAYA Youth Development' }
  ];

  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentProgramSlide, setCurrentProgramSlide] = useState(0);
  const [currentGallerySlide, setCurrentGallerySlide] = useState(0);
  const [
    currentTestimonialSlide, 
    // setCurrentTestimonialSlide
  ] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentModalImage, setCurrentModalImage] = useState(0);

  // Modal navigation handlers
  const openModal = (index) => {
    setCurrentModalImage(index);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const nextModalImage = useCallback(() => {
    setCurrentModalImage((prev) => (prev + 1) % galleryImages.length);
  }, [galleryImages.length]);

  const prevModalImage = useCallback(() => {
    setCurrentModalImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, [galleryImages.length]);

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isModalOpen) return;
      
      switch (e.key) {
        case 'Escape':
          closeModal();
          break;
        case 'ArrowLeft':
          prevModalImage();
          break;
        case 'ArrowRight':
          nextModalImage();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isModalOpen, nextModalImage, prevModalImage]);

  // Image Modal Component
  const ImageModal = () => {
    if (!isModalOpen) return null;

    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
        onClick={closeModal}
      >
        {/* Close button */}
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-black/20 hover:bg-black/40 transition-all z-50"
          aria-label="Close modal"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Content container */}
        <div 
          className="relative max-w-7xl w-full h-full flex items-center justify-center p-4"
          onClick={e => e.stopPropagation()}
        >
          {/* Previous button */}
          <button
            onClick={e => { e.stopPropagation(); prevModalImage(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 rounded-full bg-black/20 hover:bg-black/40 transition-all"
            aria-label="Previous image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Image container */}
          <div className="relative flex items-center justify-center" onClick={closeModal}>
            <img
              src={galleryImages[currentModalImage].src}
              alt={galleryImages[currentModalImage].alt}
              className="max-h-[90vh] max-w-[90vw] object-contain"
              onClick={e => e.stopPropagation()}
            />
            {/* Image counter */}
            <div 
              className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm"
              onClick={e => e.stopPropagation()}
            >
              {currentModalImage + 1} of {galleryImages.length}
            </div>
          </div>

          {/* Next button */}
          <button
            onClick={e => { e.stopPropagation(); nextModalImage(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2 rounded-full bg-black/20 hover:bg-black/40 transition-all"
            aria-label="Next image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    );
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 50);
      setShowBackToTop(scrollTop > 300);

      // Detect active section based on scroll position
      const sections = [
        { id: 'hero', element: document.querySelector('#hero') },
        { id: 'about', element: document.querySelector('#about') },
        { id: 'programs', element: document.querySelector('#programs') },
        { id: 'gallery', element: document.querySelector('#gallery') },
        { id: 'impact', element: document.querySelector('#impact') },
        { id: 'contact', element: document.querySelector('#contact') }
      ];

      // Check if we're near the bottom of the page for contact section
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPosition = scrollTop + windowHeight;
      const isNearBottom = scrollPosition >= documentHeight - 200;

      if (isNearBottom) {
        setActiveSection('contact');
      } else {
        const currentSection = sections.find(section => {
          if (section.element && section.id !== 'contact') {
            const rect = section.element.getBoundingClientRect();
            return rect.top <= 100 && rect.bottom >= 100;
          }
          return false;
        });

        if (currentSection) {
          setActiveSection(currentSection.id);
        }
      }
    };

    // Check initial scroll position on component mount
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Carousel helper functions
  const programsData = [
    {
      id: 'athletic',
      title: 'Athletic Programs',
      subtitle: 'Sports & Fitness',
      color: 'brand-red',
      image: '/soccer_0321-2.jpeg',
      description: 'Weekly basketball and soccer programs, training sessions, pick-up games, leadership opportunities, and tournaments with prizes.',
      features: ['Weekly training sessions', 'Leadership development', 'Tournaments & prizes'],
      icon: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 7.172V5L8 4z'
    },
    {
      id: 'mentorship',
      title: 'Mentorship',
      subtitle: 'Guidance & Support',
      color: 'brand-navy',
      image: '/IMG_0089-2.jpeg',
      description: 'One-on-one and group mentoring sessions focused on personal growth and development.',
      features: [
        { title: 'Individual Mentoring', desc: 'One-on-one mentoring about school, careers, and personal challenges.' },
        { title: 'Group Sessions', desc: 'Weekly group discussions about life skills, avoiding negative influences, and hearing from professional guest speakers.' }
      ],
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
    },
    {
      id: 'career',
      title: 'Career Workshops',
      subtitle: 'Professional Development',
      color: 'green-500',
      image: '/IMG_0098-2.jpeg',
      description: 'Bimonthly career workshops featuring professionals from diverse backgrounds and industries, providing networking opportunities and inspiration for youth career exploration.',
      features: ['Industry professionals', 'Networking opportunities', 'Career exploration'],
      icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6m8 0H8m0 0v-.5A1.5 1.5 0 019.5 4h5A1.5 1.5 0 0116 5.5V6M8 14h.01M12 14h.01M16 14h.01M21 12c0 4.556-4.03 8.256-9 8.256a9.48 9.48 0 01-2.593-.378l-2.985.992.637-2.906C6.269 16.76 5.776 14.426 6.286 12'
    }
  ];

  // const testimonials = [
  //   {
  //     name: 'Marcus J.',
  //     role: 'Program Participant',
  //     image: '/IMG_4777-2.jpeg',
  //     quote: 'SCAYA changed my life. The mentors helped me stay focused on school and the basketball program gave me a positive outlet. Now I\'m on track for college!'
  //   },
  //   {
  //     name: 'Sarah M.',
  //     role: 'Parent',
  //     image: '/IMG_5628.jpeg',
  //     quote: 'As a parent, seeing my daughter thrive in SCAYA\'s programs has been incredible. She\'s gained confidence and found her passion for soccer.'
  //   }
  // ];

  const nextProgramSlide = () => {
    setCurrentProgramSlide((prev) => (prev + 1) % programsData.length);
  };

  const prevProgramSlide = () => {
    setCurrentProgramSlide((prev) => (prev - 1 + programsData.length) % programsData.length);
  };

  const nextGallerySlide = () => {
    setCurrentGallerySlide((prev) => (prev + 1) % galleryImages.length);
  };

  const prevGallerySlide = () => {
    setCurrentGallerySlide((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  // const nextTestimonialSlide = () => {
  //   setCurrentTestimonialSlide((prev) => (prev + 1) % testimonials.length);
  // };

  // const prevTestimonialSlide = () => {
  //   setCurrentTestimonialSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  // };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Header */}
      <nav className={`fixed top-0 left-0 right-0 z-50 py-3 px-6 transition-all duration-500 ${
        isScrolled 
          ? 'bg-brand-navy/95 backdrop-blur-lg shadow-xl border-b border-white/10' 
          : 'bg-gradient-to-r from-transparent via-black/10 to-transparent backdrop-blur-sm'
      }`}>
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <button 
            onClick={scrollToTop}
            className="flex items-center group cursor-pointer"
            aria-label="Go to top"
          >
            <div className="relative">
              <img 
                src="/logo2.png" 
                alt="SCAYA" 
                className="h-12 w-auto filter drop-shadow-lg transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute -inset-2 bg-gradient-to-r from-brand-red/20 to-brand-navy/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
          </button>
          
          <div className="hidden md:flex items-center space-x-1 bg-white/10 backdrop-blur-md rounded-full p-1 border border-white/20">
            <a 
              href="#about" 
              className={`px-4 py-2 text-white font-medium rounded-full transition-all duration-300 hover:scale-105 ${
                activeSection === 'about' 
                  ? 'bg-brand-red text-white shadow-lg' 
                  : 'hover:bg-brand-red hover:text-white'
              }`}
            >
              About
            </a>
            <a 
              href="#programs" 
              className={`px-4 py-2 text-white font-medium rounded-full transition-all duration-300 hover:scale-105 ${
                activeSection === 'programs' 
                  ? 'bg-brand-red text-white shadow-lg' 
                  : 'hover:bg-brand-red hover:text-white'
              }`}
            >
              Programs
            </a>
            <a 
              href="#gallery" 
              className={`px-4 py-2 text-white font-medium rounded-full transition-all duration-300 hover:scale-105 ${
                activeSection === 'gallery' || activeSection === 'impact' 
                  ? 'bg-brand-red text-white shadow-lg' 
                  : 'hover:bg-brand-red hover:text-white'
              }`}
            >
              Get Involved
            </a>
            <a 
              href="#contact" 
              className={`px-4 py-2 text-white font-medium rounded-full transition-all duration-300 hover:scale-105 ${
                activeSection === 'contact' 
                  ? 'bg-brand-red text-white shadow-lg' 
                  : 'hover:bg-brand-red hover:text-white'
              }`}
            >
              Contact
            </a>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-brand-red transition-all duration-300"
            aria-label="Toggle mobile menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
        
        {/* Mobile Menu */}
        <div className={`md:hidden absolute top-full left-0 right-0 bg-brand-navy/95 backdrop-blur-lg border-t border-white/10 transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="px-6 py-4 space-y-1">
            <a 
              href="#about" 
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-4 py-3 text-white font-medium rounded-xl transition-all duration-300 ${
                activeSection === 'about' 
                  ? 'bg-brand-red text-white' 
                  : 'hover:bg-brand-red/20'
              }`}
            >
              About
            </a>
            <a 
              href="#programs" 
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-4 py-3 text-white font-medium rounded-xl transition-all duration-300 ${
                activeSection === 'programs' 
                  ? 'bg-brand-red text-white' 
                  : 'hover:bg-brand-red/20'
              }`}
            >
              Programs
            </a>
            <a 
              href="#gallery" 
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-4 py-3 text-white font-medium rounded-xl transition-all duration-300 ${
                activeSection === 'gallery' || activeSection === 'impact' 
                  ? 'bg-brand-red text-white' 
                  : 'hover:bg-brand-red/20'
              }`}
            >
              Get Involved
            </a>
            <a 
              href="#contact" 
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-4 py-3 text-white font-medium rounded-xl transition-all duration-300 ${
                activeSection === 'contact' 
                  ? 'bg-brand-red text-white' 
                  : 'hover:bg-brand-red/20'
              }`}
            >
              Contact
            </a>
          </div>
        </div>
        
        {/* Animated border */}
        <div className={`absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-red to-transparent transition-opacity duration-500 ${
          isScrolled ? 'opacity-100' : 'opacity-0'
        }`}></div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="relative h-screen bg-gradient-to-br from-brand-navy via-brand-navy-dark to-slate-900 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="absolute inset-0">
          <img 
            src="/IMG_4476-2.jpeg" 
            alt="SCAYA Community Event" 
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-brand-navy/75 via-brand-navy-dark/70 to-slate-900/65"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-1/4 left-4 lg:left-10 w-24 h-24 lg:w-32 lg:h-32 bg-brand-red/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-4 lg:right-16 w-32 h-32 lg:w-40 lg:h-40 bg-blue-400/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        <div className="relative z-10 flex items-center h-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 w-full">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center h-full min-h-[600px] pt-28 pb-20 lg:py-8">
              {/* Left Side - Logo */}
              <div className="flex flex-col items-center justify-center relative order-1 lg:order-1">
                <div className="text-center">
                  <img 
                    src="/logo2.png" 
                    alt="SCAYA - Saint Cloud Athletic Youth Association" 
                    className="h-32 sm:h-40 md:h-48 lg:h-64 w-auto mx-auto filter drop-shadow-2xl"
                  />
                  <div className="mt-4 lg:mt-8">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">SCAYA</h2>
                    <p className="text-gray-300 font-medium text-sm sm:text-base">Saint Cloud Athletic Youth Association</p>
                  </div>
                </div>
              </div>

              {/* Vertical Divider */}
              <div className="hidden lg:block absolute left-1/2 top-1/4 bottom-1/4 w-px bg-gradient-to-b from-transparent via-white/30 to-transparent transform -translate-x-1/2"></div>

              {/* Right Side - Content */}
              <div className="text-center lg:text-left text-white order-2 lg:order-2">
                <div className="mb-4">
                  <span className="inline-block px-3 py-1 bg-brand-red/20 backdrop-blur-sm border border-brand-red/30 rounded-full text-brand-red text-xs font-semibold tracking-wide uppercase">
                    Empowering Youth Since 2020
                  </span>
                </div>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black mb-4 lg:mb-6 leading-tight tracking-tight">
                  <span className="bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
                    Empowering Youth
                  </span>
                  <br />
                  <span className="bg-gradient-to-r from-brand-red via-red-400 to-brand-red bg-clip-text text-transparent">
                    Through Sports
                  </span>
                </h1>
                <p className="text-base sm:text-lg md:text-xl mb-6 lg:mb-8 text-gray-200 leading-relaxed font-light max-w-2xl mx-auto lg:mx-0">
                  Saint Cloud Athletic Youth Association creates a safe space for minority youth 
                  to grow, learn, and thrive through athletics and community support.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                  <a href="https://forms.gle/3UgkAK3nAfdrU3og7" target="_blank" rel="noopener noreferrer" className="group relative bg-gradient-to-r from-brand-red to-brand-red-dark hover:from-brand-red-dark hover:to-brand-red text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-2xl text-sm sm:text-base transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-brand-red/25 text-center">
                    <span className="relative z-10">Join Our Programs</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </a>
                  <a href="#contact" className="group bg-white/10 hover:bg-white/20 backdrop-blur-md text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-2xl text-sm sm:text-base transition-all duration-300 border-2 border-white/20 hover:border-white/40 hover:scale-105 shadow-xl">
                    <span className="flex items-center gap-2 justify-center">
                      Support Our Mission
                      <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Story Section */}
      <section id="about" className="py-16 sm:py-20 lg:py-24 bg-gradient-to-b from-white to-gray-100 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-red/20 to-brand-navy/20 rounded-3xl blur opacity-30"></div>
              <img 
                src="/first_scaya_tournament_0321-2.jpeg" 
                alt="First SCAYA Tournament March 2021" 
                className="relative rounded-3xl shadow-2xl w-full h-64 sm:h-80 lg:h-96 object-cover ring-1 ring-gray-200"
              />
              <div className="absolute -bottom-4 lg:-bottom-6 -right-4 lg:-right-6 w-24 h-24 lg:w-32 lg:h-32 bg-brand-red rounded-2xl opacity-10"></div>
            </div>
            <div className="order-1 lg:order-2 space-y-8 lg:space-y-12">
              <div>
                <div className="mb-4 lg:mb-6">
                  <span className="inline-block px-3 py-1 bg-brand-navy/10 text-brand-navy text-sm font-semibold rounded-full uppercase tracking-wide">
                    Our Mission
                  </span>
                </div>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 lg:mb-6 leading-tight">
                  <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    Empowering
                  </span>
                  <br />
                  <span className="text-brand-red">Every Youth</span>
                </h2>
                <p className="text-base sm:text-lg text-gray-600 leading-relaxed font-light">
                  The Saint Cloud Athletic Youth Association (SCAYA) is a non-profit organization that aims to empower 
                  the youth of Saint Cloud, MN to reach their full potential through athletic programs, community 
                  engagement, and promoting a safe and drug-free environment.
                </p>
              </div>
              
              <div className="relative">
                <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-brand-red to-brand-navy rounded-full"></div>
                <div className="pl-6 lg:pl-8">
                  <h3 className="text-lg sm:text-xl font-bold text-brand-red mb-3 flex items-center gap-3">
                    <span className="w-2 h-2 bg-brand-red rounded-full"></span>
                    Our Story
                  </h3>
                  <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                    SCAYA was founded in 2020 by Luqman Ali and Farah Warfa, both long-time residents of St. Cloud, MN with 
                    the aim to give youth from minority backgrounds a place to belong. Growing up ourselves in St. Cloud, we 
                    felt there were not many after-school or athletic programs tailored to mentor and support youth of 
                    minority and immigrant backgrounds.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section id="programs" className="py-16 sm:py-20 lg:py-24 bg-gray-200 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 lg:mb-20">
            <div className="mb-4 lg:mb-6">
              <span className="inline-block px-4 py-2 bg-brand-red/10 text-brand-red text-sm font-semibold rounded-full uppercase tracking-wide">
                What We Offer
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-4 lg:mb-6 leading-tight">
              <span className="bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Our Programs
              </span>
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
              We provide comprehensive athletic and mentorship programs designed to help youth develop both physically and personally.
            </p>
          </div>
          
          {/* Mobile Programs Carousel */}
          <div className="block sm:hidden relative">
            <div className="overflow-hidden rounded-xl">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentProgramSlide * 100}%)` }}
              >
                {programsData.map((program, index) => (
                  <div key={program.id} className="w-full flex-shrink-0 px-2">
                    <div className="group relative bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden h-96">
                      {/* Image Section - 50% */}
                      <div className="relative h-48">
                        <img 
                          src={program.image} 
                          alt={program.title} 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                      </div>
                      
                      {/* Content Section - 50% */}
                      <div className="p-4 h-48 flex flex-col">
                        <div className="flex items-center mb-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center mr-3 shadow-lg flex-shrink-0 ${
                            program.color === 'brand-red' ? 'bg-gradient-to-br from-brand-red to-brand-red-dark' :
                            program.color === 'brand-navy' ? 'bg-gradient-to-br from-brand-navy to-brand-navy-dark' :
                            'bg-gradient-to-br from-green-500 to-green-600'
                          }`}>
                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={program.icon} />
                            </svg>
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-base font-bold text-gray-900 mb-1">{program.title}</h3>
                            <p className={`font-semibold text-sm ${
                              program.color === 'brand-red' ? 'text-brand-red' :
                              program.color === 'brand-navy' ? 'text-brand-navy' :
                              'text-green-600'
                            }`}>{program.subtitle}</p>
                          </div>
                        </div>
                        
                        <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">
                          {program.description}
                        </p>
                        
                        {/* Key Features - Show 2 most important */}
                        <div className="space-y-2">
                          {program.id === 'mentorship' ? (
                            <div className="flex items-center">
                              <div className="w-1.5 h-1.5 bg-brand-red rounded-full mr-2 flex-shrink-0"></div>
                              <span className="text-gray-700 text-sm">Individual & Group Sessions</span>
                            </div>
                          ) : (
                            program.features.slice(0, 2).map((feature, idx) => (
                              <div key={idx} className="flex items-center">
                                <div className={`w-1.5 h-1.5 rounded-full mr-2 flex-shrink-0 ${
                                  program.color === 'brand-red' ? 'bg-brand-red' :
                                  program.color === 'brand-navy' ? 'bg-brand-navy' :
                                  'bg-green-600'
                                }`}></div>
                                <span className="text-gray-700 text-sm">{feature}</span>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Mobile Navigation */}
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={prevProgramSlide}
                className="p-2 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 group"
                aria-label="Previous program"
              >
                <svg className="w-5 h-5 text-gray-600 group-hover:text-brand-red transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div className="flex space-x-1">
                {programsData.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentProgramSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentProgramSlide ? 'bg-brand-red scale-125' : 'bg-gray-300'
                    }`}
                    aria-label={`Go to program ${index + 1}`}
                  />
                ))}
              </div>
              
              <button
                onClick={nextProgramSlide}
                className="p-2 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 group"
                aria-label="Next program"
              >
                <svg className="w-5 h-5 text-gray-600 group-hover:text-brand-red transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Desktop/Tablet Grid */}
          <div className="hidden sm:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {programsData.map((program, index) => (
              <div key={program.id} className="group relative bg-white rounded-2xl lg:rounded-3xl p-4 sm:p-6 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 lg:hover:-translate-y-3 border border-gray-100 overflow-hidden">
                {/* Background Gradient */}
                <div className={`absolute inset-0 via-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                  program.color === 'brand-red' ? 'bg-gradient-to-br from-brand-red/5 to-brand-red/10' :
                  program.color === 'brand-navy' ? 'bg-gradient-to-br from-brand-navy/5 to-brand-navy/10' :
                  'bg-gradient-to-br from-green-500/5 to-green-500/10'
                }`}></div>
                
                <div className="relative z-10">
                  <div className="flex items-center mb-4 sm:mb-6">
                    <div className="relative">
                      <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center mr-3 shadow-lg ${
                        program.color === 'brand-red' ? 'bg-gradient-to-br from-brand-red to-brand-red-dark' :
                        program.color === 'brand-navy' ? 'bg-gradient-to-br from-brand-navy to-brand-navy-dark' :
                        'bg-gradient-to-br from-green-500 to-green-600'
                      }`}>
                        <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={program.icon} />
                        </svg>
                        <div className={`absolute -top-1 -right-1 w-4 h-4 sm:w-6 sm:h-6 bg-${program.color} rounded-full animate-ping opacity-20`}></div>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">{program.title}</h3>
                      <p className={`font-semibold text-xs sm:text-sm ${
                        program.color === 'brand-red' ? 'text-brand-red' :
                        program.color === 'brand-navy' ? 'text-brand-navy' :
                        'text-green-600'
                      }`}>{program.subtitle}</p>
                    </div>
                  </div>
                  
                  <div className="relative mb-4 sm:mb-6">
                    <img 
                      src={program.image} 
                      alt={program.title} 
                      className="w-full h-32 sm:h-40 object-cover rounded-xl shadow-lg ring-1 ring-gray-200"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
                  </div>
                  
                  <p className="text-gray-600 mb-3 sm:mb-4 leading-relaxed text-xs sm:text-sm">
                    {program.description}
                  </p>
                  
                  <div className="space-y-2 sm:space-y-3">
                    {program.id === 'mentorship' ? (
                      program.features.map((feature, idx) => (
                        <div key={idx} className={`p-3 sm:p-4 bg-gradient-to-r from-${feature.title.includes('Individual') ? 'brand-navy' : 'brand-red'}/5 to-${feature.title.includes('Individual') ? 'brand-navy' : 'brand-red'}/10 rounded-xl border-l-4 border-${feature.title.includes('Individual') ? 'brand-navy' : 'brand-red'}`}>
                          <h4 className="font-bold text-gray-900 mb-2 text-xs sm:text-sm flex items-center gap-2">
                            <span className={`w-2 h-2 bg-${feature.title.includes('Individual') ? 'brand-navy' : 'brand-red'} rounded-full flex-shrink-0`}></span>
                            {feature.title}
                          </h4>
                          <p className="text-gray-600 leading-relaxed text-xs sm:text-sm">{feature.desc}</p>
                        </div>
                      ))
                    ) : (
                      program.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center p-2 bg-gray-50 rounded-lg">
                          <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-md flex items-center justify-center mr-2 flex-shrink-0 ${
                            program.color === 'brand-red' ? 'bg-brand-red/10' :
                            program.color === 'brand-navy' ? 'bg-brand-navy/10' :
                            'bg-green-500/10'
                          }`}>
                            <svg className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${
                              program.color === 'brand-red' ? 'text-brand-red' :
                              program.color === 'brand-navy' ? 'text-brand-navy' :
                              'text-green-600'
                            }`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <span className="text-gray-700 font-medium text-xs sm:text-sm">{feature}</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 lg:mb-4">See Us in Action</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600">Moments that capture the spirit of our community</p>
          </div>
          
          {/* Mobile Gallery Carousel */}
          <div className="block sm:hidden relative max-w-5xl mx-auto">
            <div className="overflow-hidden rounded-2xl">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentGallerySlide * 100}%)` }}
              >
                {galleryImages.map((image, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-2">
                    <div 
                      className="relative group overflow-hidden rounded-xl shadow-2xl cursor-pointer"
                      onClick={() => openModal(index)}
                    >
                      <img 
                        src={image.src} 
                        alt={image.alt} 
                        className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      <div className="absolute bottom-6 left-6 right-6">
                        <h3 className="text-white text-lg font-bold mb-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          {image.alt}
                        </h3>
                        <div className="w-12 h-1 bg-brand-red rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Mobile Navigation */}
            <div className="flex items-center justify-between mt-8">
              <button
                onClick={prevGallerySlide}
                className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border border-gray-200 group"
                aria-label="Previous image"
              >
                <svg className="w-6 h-6 text-gray-600 group-hover:text-brand-red transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div className="flex space-x-2">
                {galleryImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentGallerySlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentGallerySlide ? 'bg-brand-red scale-125' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
              
              <button
                onClick={nextGallerySlide}
                className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border border-gray-200 group"
                aria-label="Next image"
              >
                <svg className="w-6 h-6 text-gray-600 group-hover:text-brand-red transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>

          {/* Desktop/Tablet Gallery Grid */}
          <div className="hidden sm:block">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {galleryImages.map((image, index) => (
                <div 
                  key={index} 
                  className="relative group overflow-hidden rounded-xl cursor-pointer transform transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
                  onClick={() => openModal(index)}
                >
                  <img 
                    src={image.src} 
                    alt={image.alt} 
                    className="w-full h-48 md:h-56 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-white text-sm font-medium leading-tight">
                      {image.alt}
                    </h3>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m4-3H6" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Modal */}
          <ImageModal />
        </div>
      </section>

      {/* Impact Section */}
      <section id="impact" className="py-16 sm:py-20 bg-gradient-to-br from-brand-gray-50 to-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 lg:mb-4">Our Impact</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600">Making a difference in young lives every day</p>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-12 lg:mb-16">
            <div className="text-center bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-md">
              <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-brand-red mb-1 lg:mb-2">150+</div>
              <div className="text-gray-700 font-medium text-xs sm:text-sm lg:text-base">Youth Served</div>
            </div>
            <div className="text-center bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-md">
              <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-brand-navy mb-1 lg:mb-2">5</div>
              <div className="text-gray-700 font-medium text-xs sm:text-sm lg:text-base">Years Strong</div>
            </div>
            <div className="text-center bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-md">
              <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-brand-red mb-1 lg:mb-2">24</div>
              <div className="text-gray-700 font-medium text-xs sm:text-sm lg:text-base">Weekly Sessions</div>
            </div>
            <div className="text-center bg-white rounded-xl lg:rounded-2xl p-4 lg:p-6 shadow-md">
              <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-brand-navy mb-1 lg:mb-2">countless</div>
              <div className="text-gray-700 font-medium text-xs sm:text-sm lg:text-base">Positive Outcomes</div>
            </div>
          </div>

          {/* Mobile Testimonials Carousel */}
          <div className="block sm:hidden relative max-w-4xl mx-auto">
            <div className="overflow-hidden rounded-2xl lg:rounded-3xl">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentTestimonialSlide * 100}%)` }}
              >
                {/* {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-4">
                    <div className="bg-white rounded-xl lg:rounded-2xl p-8 lg:p-12 shadow-2xl border border-gray-100">
                      <div className="text-center">
                        <div className="relative mb-6">
                          <img 
                            src={testimonial.image} 
                            alt={`${testimonial.name} testimonial`} 
                            className="w-20 h-20 lg:w-24 lg:h-24 rounded-full object-cover mx-auto shadow-lg ring-4 ring-white"
                          />
                          <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-brand-red rounded-full flex items-center justify-center">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                        
                        <div className="text-yellow-400 mb-4 text-xl">★★★★★</div>
                        
                        <blockquote className="text-gray-700 italic mb-6 text-lg lg:text-xl leading-relaxed">
                          "{testimonial.quote}"
                        </blockquote>
                        
                        <div className="space-y-1">
                          <div className="font-bold text-gray-900 text-lg lg:text-xl">{testimonial.name}</div>
                          <div className="text-brand-red font-semibold">{testimonial.role}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))} */}
              </div>
            </div>
            
            {/* Navigation */}
            <div className="flex items-center justify-between mt-8">
              {/* <button
                onClick={prevTestimonialSlide}
                className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border border-gray-200 group"
                aria-label="Previous testimonial"
              >
                <svg className="w-6 h-6 text-gray-600 group-hover:text-brand-red transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button> */}
              
              <div className="flex space-x-2">
                {/* {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonialSlide(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      index === currentTestimonialSlide ? 'bg-brand-red scale-125' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))} */}
              </div>
              
              {/* <button
                onClick={nextTestimonialSlide}
                className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 border border-gray-200 group"
                aria-label="Next testimonial"
              >
                <svg className="w-6 h-6 text-gray-600 group-hover:text-brand-red transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button> */}
            </div>
          </div>

          {/* Desktop/Tablet Testimonials Grid */}
          <div className="hidden sm:grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {/* {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl lg:rounded-2xl p-6 lg:p-8 shadow-lg">
                <div className="flex flex-col sm:flex-row sm:items-start mb-4 lg:mb-6">
                  <img 
                    src={testimonial.image} 
                    alt={`${testimonial.name} testimonial`} 
                    className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover mb-3 sm:mb-0 sm:mr-4 mx-auto sm:mx-0 flex-shrink-0"
                  />
                  <div className="text-center sm:text-left">
                    <div className="text-yellow-400 mb-2 text-sm sm:text-base">★★★★★</div>
                    <p className="text-gray-700 italic mb-3 lg:mb-4 text-sm sm:text-base leading-relaxed">
                      "{testimonial.quote}"
                    </p>
                    <div className="font-semibold text-gray-900 text-sm sm:text-base">{testimonial.name}</div>
                    <div className="text-gray-600 text-xs sm:text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))} */}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="relative py-16 sm:py-20 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/soccer_0625-2.jpeg" 
            alt="Soccer Game June 2025" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy/85 to-brand-navy-dark/75"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 lg:mb-6">
            Ready to Make a Difference?
          </h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-100 mb-6 lg:mb-8 leading-relaxed max-w-3xl mx-auto">
            Join us in empowering the next generation of leaders. Whether you want to participate, 
            volunteer, or support our mission, there's a place for you at SCAYA.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center max-w-lg sm:max-w-none mx-auto">
            <a href="https://forms.gle/3UgkAK3nAfdrU3og7" target="_blank" rel="noopener noreferrer" className="bg-brand-red hover:bg-brand-red-dark text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl lg:rounded-2xl text-sm sm:text-base lg:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg text-center">
              Join Our Programs
            </a>
            <a href="#contact" className="bg-white hover:bg-gray-100 text-brand-navy font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl lg:rounded-2xl text-sm sm:text-base lg:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg text-center">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-brand-navy-dark text-white py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {/* Logo & Mission */}
            <div className="sm:col-span-2 lg:col-span-2">
              <div className="mb-4">
                <img 
                  src="/logo2.png" 
                  alt="SCAYA - Saint Cloud Athletic Youth Association" 
                  className="h-16 lg:h-20 w-auto"
                />
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed text-sm lg:text-base">
                Saint Cloud Athletic Youth Association - Empowering youth through athletics, 
                mentorship, and community engagement since 2020.
              </p>
              <div className="flex space-x-3 lg:space-x-4 justify-center sm:justify-start">
                <a target="_blank" rel="noreferrer" href="https://facebook.com/scaya2020" className="w-10 h-10 lg:w-12 lg:h-12 bg-brand-red rounded-full flex items-center justify-center hover:bg-brand-red-dark transition-colors">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M20 10C20 4.477 15.523 0 10 0S0 4.477 0 10c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V10h2.54V7.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V10h2.773l-.443 2.89h-2.33v6.988C16.343 19.128 20 14.991 20 10z" clipRule="evenodd" />
                  </svg>
                </a>
                <a target="_blank" rel="noreferrer" href="https://snapchat.com/add/scaya4342" className="w-10 h-10 lg:w-12 lg:h-12 bg-brand-red rounded-full flex items-center justify-center hover:bg-brand-red-dark transition-colors">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.166 3c.796 0 2.302.144 3.049 1.662.559 1.133.559 3.112.559 4.114 0 .47.162.863.42 1.207.417.559.977.859 1.462 1.087.252.119.462.217.587.315.267.21.427.49.427.826 0 .244-.098.524-.315.738-.231.229-.553.343-.847.418-.588.149-1.315.25-2.068.33.042.343.119.735.315 1.186.63 1.449 2.4 1.449 2.4s-.294.126-.497.295c-.406.336-.637.749-.693 1.186-.056.434.035.798.245 1.095.245.347.644.588 1.049.693.098.028.196.056.273.105.42.245.546.742.315 1.165-.224.42-.658.63-1.067.504-2.219-.693-3.535-2.268-4.123-3.724-.609 1.456-1.925 3.031-4.144 3.724-.406.126-.84-.084-1.064-.504-.231-.423-.105-.92.315-1.165.077-.049.175-.077.273-.105.406-.105.805-.346 1.05-.693.21-.297.301-.661.245-1.095-.056-.437-.287-.85-.693-1.186-.203-.169-.497-.295-.497-.295s1.77 0 2.4-1.449c.196-.451.273-.843.315-1.186-.753-.08-1.48-.181-2.068-.33-.294-.075-.616-.189-.847-.418-.217-.214-.315-.494-.315-.738 0-.336.16-.616.427-.826.125-.098.335-.196.587-.315.485-.228 1.045-.528 1.462-1.087.258-.344.42-.737.42-1.207 0-1.002 0-2.981.559-4.114C9.864 3.144 11.37 3 12.166 3z"/>
                  </svg>
                </a>
                <a target="_blank" rel="noreferrer" href="https://instagram.com/scaya20" className="w-10 h-10 lg:w-12 lg:h-12 bg-brand-red rounded-full flex items-center justify-center hover:bg-brand-red-dark transition-colors">
                  <svg className="w-5 h-5 lg:w-6 lg:h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.017 0C18.396 0 20.006 1.614 20.006 8.001v3.998C20.006 18.396 18.396 20.006 12.017 20.006H8.001C1.622 20.006.006 18.396.006 12.017V8.001C.006 1.614 1.622 0 8.001 0h4.016zm3.982 6.002c-.702 0-1.027.422-1.027.998v4.996c0 .578.325 1.002 1.027 1.002.703 0 1.028-.424 1.028-1.002V7c0-.576-.325-.998-1.028-.998zM10 6c-2.229 0-4 1.771-4 4s1.771 4 4 4 4-1.771 4-4-1.771-4-4-4zm0 1.5c1.38 0 2.5 1.12 2.5 2.5s-1.12 2.5-2.5 2.5S7.5 11.38 7.5 10s1.12-2.5 2.5-2.5z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="text-center sm:text-left">
              <h4 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-300 text-sm lg:text-base">
                <li><a href="/about#about" className="hover:text-white transition-colors block py-1">About Us</a></li>
                <li><a href="/about#programs" className="hover:text-white transition-colors block py-1">Programs</a></li>
                <li><a href="/get-involved#programs" className="hover:text-white transition-colors block py-1">Get Involved</a></li>
                <li><a href="/about#contact" className="hover:text-white transition-colors block py-1">Contact</a></li>
                <li><a href="https://forms.gle/3UgkAK3nAfdrU3og7" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors block py-1">Apply Today</a></li>
              </ul>
            </div>

            {/* Contact & Newsletter */}
            <div className="text-center sm:text-left">
              <h4 className="text-base lg:text-lg font-semibold mb-3 lg:mb-4">Stay Connected</h4>
              <div className="space-y-2 text-gray-300 mb-4 lg:mb-6 text-sm lg:text-base">
                <p className="break-words">📧 scaya2020@gmail.org</p>
                <p className="break-words leading-relaxed">📍 3333 West Division St Ste 218-8 Saint Cloud, MN 56301</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 lg:mt-12 pt-6 lg:pt-8 text-center text-gray-400">
            <p className="text-xs lg:text-sm">&copy; 2025 Saint Cloud Athletic Youth Association. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Back to Top FAB */}
      {showBackToTop && !isModalOpen && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-4 right-4 lg:bottom-8 lg:right-8 z-50 w-12 h-12 lg:w-14 lg:h-14 rounded-full shadow-xl transition-all duration-300 transform hover:scale-110 hover:shadow-2xl backdrop-blur-md border border-white/20 bg-brand-red hover:bg-brand-red-dark text-white touch-manipulation"
          aria-label="Back to top"
        >
          <svg
            className="w-5 h-5 lg:w-6 lg:h-6 mx-auto transition-transform duration-300 hover:-translate-y-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default LandingPage;
